import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, ArrowLeft, CheckCircle2, RotateCcw } from "lucide-react";

const STATUS_LABEL = {
  submitted: { label: "Enviada", color: "bg-blue-100 text-blue-700" },
  ado_pending: { label: "Pendiente en ADO", color: "bg-amber-100 text-amber-700" },
  triaged: { label: "Triada", color: "bg-indigo-100 text-indigo-700" },
  in_progress: { label: "En progreso", color: "bg-purple-100 text-purple-700" },
  done: { label: "Completada", color: "bg-green-100 text-green-700" },
  rejected: { label: "Rechazada", color: "bg-red-100 text-red-700" },
};

export default function SeguimientoDemanda() {
  const { id } = useParams();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [showReopenForm, setShowReopenForm] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/demanda/solicitudes/${id}`);
      setDoc(data);
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo cargar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iter313 · O08 — cerrar el ciclo: el solicitante confirma o reabre la entrega
  const handleAccept = async () => {
    setActing(true);
    try {
      await axios.post(`${API}/demanda/solicitudes/${id}/accept`);
      toast.success("Entrega confirmada. ¡Gracias!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo confirmar"); }
    finally { setActing(false); }
  };

  const handleReopen = async () => {
    setActing(true);
    try {
      await axios.post(`${API}/demanda/solicitudes/${id}/reopen`, { reason: reopenReason });
      toast.success("Solicitud reabierta");
      setShowReopenForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo reabrir"); }
    finally { setActing(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-[#007934]" size={24} /></div>;
  if (error) return <div className="p-6 max-w-xl mx-auto"><p className="text-red-500 text-sm" data-testid="seguimiento-demanda-error">{error}</p></div>;

  const st = STATUS_LABEL[doc.status] || STATUS_LABEL.submitted;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4" data-testid="seguimiento-demanda-page">
      <Link to="/demanda" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"><ArrowLeft size={14} /> Volver</Link>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">{doc.titulo}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{doc.solicitante} · {new Date(doc.created_at).toLocaleDateString("es-ES")}</p>
          </div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.color}`}>{st.label}</span>
        </div>
        {doc.descripcion && <p className="text-sm text-gray-600 mt-3">{doc.descripcion}</p>}
        <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
          <div><p className="text-xs text-gray-400">Track</p><p className="font-medium text-gray-800">{doc.track === "fast_track" ? "Fast track" : "Desarrollo"}</p></div>
          <div><p className="text-xs text-gray-400">Prioridad</p><p className="font-medium text-gray-800">{doc.prioridad}</p></div>
          <div><p className="text-xs text-gray-400">Proyecto ADO</p><p className="font-medium text-gray-800">{doc.ado_project}</p></div>
          {doc.sla_due_at && <div><p className="text-xs text-gray-400">SLA</p><p className="font-medium text-gray-800">{new Date(doc.sla_due_at).toLocaleString("es-ES")}</p></div>}
          {doc.iniciativa_titulo && <div><p className="text-xs text-gray-400">Iniciativa</p><p className="font-medium text-gray-800">{doc.iniciativa_titulo}{doc.iniciativa_azure_id ? ` (#${doc.iniciativa_azure_id})` : ""}</p></div>}
        </div>
        {doc.azure_id ? (
          <a href={doc.azure_url} target="_blank" rel="noreferrer" data-testid="seguimiento-azure-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#007934] hover:underline mt-4">
            Ver Work Item #{doc.azure_id} en Azure DevOps <ExternalLink size={13} />
          </a>
        ) : (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700 mt-4">
            Aún sin work item en Azure DevOps{doc.ado_error ? `: ${doc.ado_error}` : ""}.
          </div>
        )}

        {/* Iter313 · O08 — aceptación del solicitante: IT marca "done", pero
            quien pidió la solicitud confirma que le sirve (o la reabre). */}
        {doc.status === "done" && doc.solicitante_email === user?.email && doc.requester_accepted !== true && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4" data-testid="acceptance-panel">
            <p className="text-sm text-green-800 font-medium mb-3">IT ha marcado esta solicitud como completada. ¿Confirmas que la entrega te sirve?</p>
            <div className="flex gap-2">
              <button onClick={handleAccept} disabled={acting} data-testid="accept-delivery-btn"
                className="flex items-center gap-1.5 text-sm font-semibold bg-[#007934] text-white rounded-lg px-3 py-2 hover:bg-[#006228] disabled:opacity-60">
                <CheckCircle2 size={14} /> Sí, confirmo la entrega
              </button>
              <button onClick={() => setShowReopenForm(s => !s)} data-testid="reopen-delivery-btn"
                className="flex items-center gap-1.5 text-sm font-semibold text-amber-700 border border-amber-300 rounded-lg px-3 py-2 hover:bg-amber-50">
                <RotateCcw size={14} /> No me sirve, reabrir
              </button>
            </div>
            {showReopenForm && (
              <div className="mt-3 space-y-2">
                <textarea value={reopenReason} onChange={e => setReopenReason(e.target.value)}
                  placeholder="¿Qué falta o qué no funciona como esperabas?"
                  data-testid="reopen-reason-input"
                  className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm" rows={2} />
                <button onClick={handleReopen} disabled={acting || !reopenReason.trim()} data-testid="confirm-reopen-btn"
                  className="text-xs font-semibold bg-amber-600 text-white rounded-lg px-3 py-1.5 hover:bg-amber-700 disabled:opacity-50">
                  Confirmar reapertura
                </button>
              </div>
            )}
          </div>
        )}
        {doc.requester_accepted === true && (
          <div className="flex items-center gap-1.5 text-sm text-green-700 mt-4" data-testid="accepted-note">
            <CheckCircle2 size={14} /> Entrega confirmada por el solicitante{doc.requester_accepted_at ? ` el ${new Date(doc.requester_accepted_at).toLocaleDateString("es-ES")}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}
